import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../../shared/api/axiosInstance';
import type { DeletionImpact } from '@/generated/api/models/deletionImpact';
import { productCategoryKeys } from './useProductCategories';
import type { Product, ProductType, ProductStatus, ProductRole, ProductVariant } from '../types';

interface ProductCreate {
  name: string;
  type: ProductType;
  // Optional — the backend defaults a blank/omitted value to 'unit'.
  base_unit_name?: string;
  product_category_ids?: string[];
  sku?: string | null;
  description?: string | null;
  /** Brand / manufacturer (Shopify "Vendor"). */
  brand?: string | null;
  base_price?: number | null;
  cost_price?: number | null;
  billing_cycle?: string | null;
  pricing_model?: string | null;
  service_billing_model?: string | null;
  service_duration_hours?: number | null;
  tax_category?: string | null;
  is_taxable?: boolean;
  url?: string | null;
  hs_code?: string | null;
  image_id?: string | null;
  // Physical product dimensions (stored in metric)
  weight_kg?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  // Per-record unit overrides (Runtime Units of Measure, Tier A).
  weight_unit?: 'kg' | 'lb' | null;
  dimension_unit?: 'cm' | 'in' | null;
  status?: ProductStatus;
  /** Buy/sell/both. Omitted on manual create ⇒ backend defaults to 'sell'. */
  product_role?: ProductRole;
  company_ids?: string[];
  /** Tenant-defined custom fields (P4); validated server-side against definitions. */
  custom_fields?: Record<string, unknown>;
}

interface ProductUpdate extends Partial<Omit<ProductCreate, 'product_category_ids'>> {
  /** Category memberships. null = unchanged, [] = clear all, [id] = single category. */
  product_category_ids?: string[] | null;
  clear_image?: boolean; // Set to true to remove the product's image
}

interface VariantCreate {
  name?: string | null;
  sku?: string | null;
  /** One dimension value per active product dimension. */
  option_value_ids?: string[];
  weight_kg?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  /** Per-variant unit cost (Shopify "Cost per item"). */
  cost?: number | null;
  /** Per-variant MSRP / list price (Shopify "Compare At Price"). */
  compare_at_price?: number | null;
  /** Per-variant GTIN/UPC/EAN/ISBN barcode. */
  barcode?: string | null;
  image_id?: string | null;
  status?: string | null;
}

/** Update adds `clear_image` to detach the variant's image (image_id=null is a no-op server-side). */
type VariantUpdate = Partial<VariantCreate> & { clear_image?: boolean };

/** A single combination for batch variant generation. */
export interface VariantCombination {
  value_ids: string[];
}

interface ListProductsParams {
  category_id?: string;
  type?: ProductType;
  /** Buy/sell filter. 'sell' shows sold + both, 'buy' shows purchased + both. */
  product_role?: ProductRole;
  status?: ProductStatus;
  search?: string;
  includeCategories?: boolean;
  /** JSON object of custom-field filters, e.g. '{"material":"steel"}' (P5). */
  custom_fields?: string;
  /** Comma-separated tag UUIDs (cross-cutting tags framework). */
  tags?: string;
  /** Tag matching mode: 'any' (OR, default) or 'all' (AND). */
  tags_mode?: 'any' | 'all';
  /** `'own'` when a Full-Access admin narrows a governed list to their own
   * owner/shared scope via `useAdminListScope`; undefined for everyone else. */
  admin_scope?: string;
}

export const productKeys = {
  all: ['products'] as const,
  list: (params?: ListProductsParams) => [...productKeys.all, 'list', params] as const,
  detail: (id: string) => [...productKeys.all, 'detail', id] as const,
  variants: (productId: string) => [...productKeys.all, productId, 'variants'] as const,
  dimensions: (productId: string) => [...productKeys.all, productId, 'dimensions'] as const,
  dimensionLibrary: () => [...productKeys.all, 'dimension-library'] as const,
  configurations: (productId: string) => [...productKeys.all, productId, 'configurations'] as const,
  categoryConfigurations: (categoryId: string) =>
    [...productKeys.all, 'category', categoryId, 'configurations'] as const,
};

function cleanParams(params?: ListProductsParams): ListProductsParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as ListProductsParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function useProducts(params?: ListProductsParams) {
  /* eslint-disable react-hooks/exhaustive-deps */
  // Intentionally only tracking specific param fields to prevent unnecessary re-renders
  const stableParams = useMemo(
    () => cleanParams(params),
    [params?.category_id, params?.type, params?.product_role, params?.status, params?.search, params?.includeCategories, params?.custom_fields, params?.tags, params?.tags_mode, params?.admin_scope]
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  return useQuery({
    queryKey: productKeys.list(stableParams),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (stableParams?.category_id) queryParams.append('category_id', stableParams.category_id);
      if (stableParams?.type) queryParams.append('type', stableParams.type);
      if (stableParams?.product_role) queryParams.append('product_role', stableParams.product_role);
      if (stableParams?.status) queryParams.append('status', stableParams.status);
      if (stableParams?.search) queryParams.append('search', stableParams.search);
      if (stableParams?.includeCategories) queryParams.append('include_categories', 'true');
      if (stableParams?.custom_fields) queryParams.append('custom_fields', stableParams.custom_fields);
      if (stableParams?.tags) queryParams.append('tags', stableParams.tags);
      if (stableParams?.tags_mode) queryParams.append('tags_mode', stableParams.tags_mode);
      if (stableParams?.admin_scope) queryParams.append('admin_scope', stableParams.admin_scope);

      const url = `/api/v1/master-data/products/products${queryParams.toString() ? `?${queryParams}` : ''}`;
      const response = await axiosInstance.get<{ data: Product[] }>(url);
      return response.data.data;
    },
  });
}

export function useProduct(id: string, options?: { includeVariants?: boolean; includeConfigurations?: boolean; includeCategories?: boolean }) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (options?.includeVariants) queryParams.append('include_variants', 'true');
      if (options?.includeConfigurations) queryParams.append('include_configurations', 'true');
      if (options?.includeCategories) queryParams.append('include_categories', 'true');

      const url = `/api/v1/master-data/products/products/${id}${queryParams.toString() ? `?${queryParams}` : ''}`;
      const response = await axiosInstance.get<{ data: Product }>(url);
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ProductCreate) => {
      const response = await axiosInstance.post<{ data: Product }>('/api/v1/master-data/products/products', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      // A product can be created into a category — refresh category member counts.
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProductUpdate }) => {
      const response = await axiosInstance.put<{ data: Product }>(`/api/v1/master-data/products/products/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      // product_category_ids may have changed — refresh category member counts so the
      // families list and pickers don't show a stale count.
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
    },
  });
}

/** Preview what deleting a {{product}} will affect (blockers / cascades / options). */
export function useProductDeletionImpact(id: string | null) {
  return useQuery({
    queryKey: [...productKeys.all, 'deletion-impact', id],
    queryFn: async () => {
      const response = await axiosInstance.get<DeletionImpact>(
        `/api/v1/master-data/products/products/${id}/deletion-impact`
      );
      return response.data;
    },
    enabled: !!id,
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, options }: { id: string; options?: Record<string, boolean> }) => {
      await axiosInstance.delete(`/api/v1/master-data/products/products/${id}`, {
        data: options ? { options } : undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      // Deleting a category member changes its category's count.
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
      // Deletion can zero-out / detach stock — refresh the inventory stock-levels list.
      queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
    },
  });
}

export function useBulkUpdateProductStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: ProductStatus }) => {
      const response = await axiosInstance.post<{ updated: number }>(
        '/api/v1/master-data/products/products/bulk-update',
        { ids, status }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useBulkDeleteProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, options }: { ids: string[]; options?: Record<string, boolean> }) => {
      const response = await axiosInstance.post<{ deleted: number }>(
        '/api/v1/master-data/products/products/bulk-delete',
        { ids, options }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      // Deletion can zero-out / detach stock — refresh the inventory stock-levels list.
      queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
    },
  });
}

export function useArchiveProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axiosInstance.post<{ data: Product }>(`/api/v1/master-data/products/products/${id}/archive`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

// Variant Hooks

export function useProductVariants(productId: string) {
  return useQuery({
    queryKey: productKeys.variants(productId),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductVariant[] }>(
        `/api/v1/master-data/products/products/${productId}/variants`
      );
      return response.data.data;
    },
    enabled: !!productId,
  });
}

export function useCreateVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, data }: { productId: string; data: VariantCreate }) => {
      const response = await axiosInstance.post<{ data: ProductVariant }>(
        `/api/v1/master-data/products/products/${productId}/variants`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.variants(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
    },
  });
}

export function useGenerateVariants() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      combinations,
    }: {
      productId: string;
      combinations: VariantCombination[];
    }) => {
      const response = await axiosInstance.post<{ data: ProductVariant[] }>(
        `/api/v1/master-data/products/products/${productId}/variants/generate`,
        { combinations }
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.variants(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
    },
  });
}

export function useResolveVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      selections,
    }: {
      productId: string;
      /** Maps each variant dimension id to the chosen value id. */
      selections: Record<string, string>;
    }) => {
      const response = await axiosInstance.post<{ data: ProductVariant }>(
        `/api/v1/master-data/products/products/${productId}/variants/resolve`,
        { selections }
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.variants(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
    },
  });
}

export function useUpdateVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ variantId, data }: { variantId: string; data: VariantUpdate }) => {
      const response = await axiosInstance.put<{ data: ProductVariant }>(
        `/api/v1/master-data/products/variants/${variantId}`,
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useDeleteVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variantId: string) => {
      await axiosInstance.delete(`/api/v1/master-data/products/variants/${variantId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}
